import http from '@/lib/http'
import type { TimelineEvent, TimelineEventCategory, TimelineEventSource, TimelineFieldChange } from '@/types/timeline'
import { getNoteTypeConfig } from '@/components/shared/timeline/noteTypeConfig'
import {
  parseFieldsUpdate,
  parseCrmDifferences,
  parseRefund,
  parseSentBy,
  parseStatusUpdate,
  parseMerge,
  parseLink
} from '@/components/shared/timeline/noteParser'
import { useQuery } from '@tanstack/react-query'

export type APITimelineEntry = {
  note: string
  note_type: string
  created_at: string
  action_by_id?: string | null
  action_by_name?: string | null
}

type APITimelineResponse = {
  data: APITimelineEntry[]
  total: number
}

function mapSource(entry: APITimelineEntry): TimelineEventSource {
  if ((!entry.action_by_name && !entry.action_by_id) || !entry.action_by_id?.startsWith('user_')) return 'System'
  return 'Manual'
}

function mapUser(entry: APITimelineEntry): string {
  if (entry.action_by_name) {
    return entry.action_by_name
  }

  if (entry.action_by_id?.startsWith('user_')) {
    return 'Unknown User'
  }

  return entry.action_by_id ? `Temporal (${entry.action_by_id})` : 'System'
}

/**
 * Communications notes carry their sender in the note body (`Sent by: ...`)
 * because the Messaging-queue path doesn't authenticate as the original user
 * against PHP. Prefer that when present; fall back to `action_by_*` mapping.
 */
function resolveSourceAndUser(entry: APITimelineEntry): {
  source: TimelineEventSource
  user: string
} {
  if (entry.note_type === 'Communications') {
    const sentBy = parseSentBy(entry.note)
    if (sentBy) return { source: sentBy.source, user: sentBy.name }
  }
  return { source: mapSource(entry), user: mapUser(entry) }
}

function buildPatientTimelineDescription(
  noteType: string,
  note: string,
  configTitle: string,
  fieldChanges?: TimelineFieldChange[]
): string {
  switch (noteType) {
    case 'fields_update':
    case 'update': {
      const parsed = fieldChanges ?? parseFieldsUpdate(note)
      if (parsed) {
        return parsed.length === 1 ? `${parsed[0].field} updated` : `${parsed.length} fields updated`
      }
      break
    }
    case 'status_update': {
      const parsed = parseStatusUpdate(note)
      if (parsed) return `${parsed.previous} \u2192 ${parsed.current}`
      break
    }
    case 'email': {
      if (note) return note
      break
    }
    case 'merge': {
      const parsed = parseMerge(note)
      if (parsed) return `Merged with patient ${parsed.lostPatientRecordHash}`
      break
    }
    case 'link': {
      const parsed = parseLink(note)
      if (parsed) return `Linked patients: ${parsed.linkedPatients.join(', ')}`
      break
    }
    case 'create_patient_crm': {
      return 'Patient created from CRM'
    }
    case 'update_patient_crm': {
      const parsed = fieldChanges ?? parseCrmDifferences(note)
      if (parsed) {
        return parsed.length === 1 ? `${parsed[0].field} updated from CRM` : `${parsed.length} fields updated from CRM`
      }
      break
    }
    case 'create_patient': {
      if (note) return note
      break
    }
    case 'merge_task_automatic_created': {
      return 'Automatic merge of duplicate patient'
    }
  }

  return note || configTitle
}

const ORDER_TITLE_OVERRIDES: Record<string, string> = {
  fields_update: 'Order Updated',
  update: 'Order Updated',
  status_update: 'Order Status Changed',
  rest: 'Manual Entry'
}

const ORDER_CATEGORY_OVERRIDES: Record<string, TimelineEventCategory> = {
  fields_update: 'Order',
  update: 'Order',
  status_update: 'Order',
  rest: 'Order'
}

const MED_REQUEST_TITLE_OVERRIDES: Record<string, string> = {
  fields_update: 'Medication Request Updated',
  update: 'Medication Request Updated',
  status_update: 'Medication Request Status Changed',
  rest: 'Manual Entry'
}

const MED_REQUEST_CATEGORY_OVERRIDES: Record<string, TimelineEventCategory> = {
  fields_update: 'Medication Requests',
  update: 'Medication Requests',
  status_update: 'Medication Requests',
  rest: 'Medication Requests',
  id_verification: 'Medication Requests',
  id_verification_reset: 'Medication Requests'
}

function buildOrderTimelineDescription(
  noteType: string,
  note: string,
  configTitle: string,
  fieldChanges?: TimelineFieldChange[]
): string {
  switch (noteType) {
    case 'fields_update':
    case 'update': {
      const parsed = fieldChanges ?? parseFieldsUpdate(note)
      if (parsed) {
        return parsed.length === 1 ? `${parsed[0].field} updated` : `${parsed.length} fields updated`
      }
      break
    }
    case 'status_update': {
      const parsed = parseStatusUpdate(note)
      if (parsed) return `${parsed.previous} \u2192 ${parsed.current}`
      break
    }
    case 'email': {
      if (note) return note
      break
    }
    case 'refund': {
      const parsed = parseRefund(note)
      if (parsed) return `Refund of £${Number(parsed.amount).toFixed(2)}`
      break
    }
  }

  return note || configTitle
}

function toPatientTimelineEvent(entry: APITimelineEntry, index: number, patientHash: string): TimelineEvent {
  const config = getNoteTypeConfig(entry.note_type)

  let fieldChanges: TimelineFieldChange[] | undefined
  if (entry.note_type === 'fields_update' || entry.note_type === 'update') {
    fieldChanges = parseFieldsUpdate(entry.note) ?? undefined
  } else if (entry.note_type === 'update_patient_crm') {
    fieldChanges = parseCrmDifferences(entry.note) ?? undefined
  }

  const description = buildPatientTimelineDescription(entry.note_type, entry.note, config.title, fieldChanges)
  const { source, user } = resolveSourceAndUser(entry)

  return {
    id: `patient-${patientHash}-${index}`,
    category: config.category,
    source,
    title: config.title,
    description,
    timestamp: new Date(entry.created_at),
    user,
    fieldChanges,
    noteType: entry.note_type,
    note: entry.note
  }
}

function toOrderTimelineEvent(entry: APITimelineEntry, index: number, orderHash: string): TimelineEvent {
  const config = getNoteTypeConfig(entry.note_type)
  const title = ORDER_TITLE_OVERRIDES[entry.note_type] ?? config.title
  const category = ORDER_CATEGORY_OVERRIDES[entry.note_type] ?? config.category

  let fieldChanges: TimelineFieldChange[] | undefined
  if (entry.note_type === 'fields_update' || entry.note_type === 'update') {
    fieldChanges = parseFieldsUpdate(entry.note) ?? undefined
  }

  const description = buildOrderTimelineDescription(entry.note_type, entry.note, title, fieldChanges)
  const { source, user } = resolveSourceAndUser(entry)

  return {
    id: `order-${orderHash}-${index}`,
    category,
    source,
    title,
    description,
    timestamp: new Date(entry.created_at),
    user,
    fieldChanges,
    noteType: entry.note_type,
    note: entry.note
  }
}

const MED_REQUEST_FIELD_LABELS: Record<string, string> = {
  cs_notes: 'Customer service notes',
  notes: 'Pharmacist notes',
  dose: 'Dose',
  directions: 'Directions',
  quantity: 'Quantity',
  reason_for_hold: 'Reason for hold',
  reason_for_rejection: 'Reason for rejection',
  justification: 'Justification'
}

function formatFieldLabel(field: string): string {
  return MED_REQUEST_FIELD_LABELS[field] ?? field.replace(/_/g, ' ')
}

const CS_NOTES_CATEGORY: TimelineEventCategory = 'CS notes'
const CS_NOTES_TITLE = 'CS Notes Updated'

function buildMedRequestTimelineDescription(
  noteType: string,
  note: string,
  configTitle: string,
  fieldChanges?: TimelineFieldChange[]
): string {
  switch (noteType) {
    case 'fields_update':
    case 'update': {
      const parsed = fieldChanges ?? parseFieldsUpdate(note)
      if (parsed) {
        return parsed.length === 1 ? `${formatFieldLabel(parsed[0].field)} updated` : `${parsed.length} fields updated`
      }
      break
    }
    case 'status_update': {
      const parsed = parseStatusUpdate(note)
      if (parsed) return `${parsed.previous} \u2192 ${parsed.current}`
      break
    }
    case 'email': {
      if (note) return note
      break
    }
  }

  return note || configTitle
}

function isCsNotesUpdate(noteType: string, fieldChanges: TimelineFieldChange[] | undefined): boolean {
  if (noteType !== 'fields_update' && noteType !== 'update') return false
  return !!fieldChanges?.some(fc => fc.field === 'cs_notes')
}

function toMedRequestTimelineEvent(entry: APITimelineEntry, index: number, medReqHash: string): TimelineEvent {
  const config = getNoteTypeConfig(entry.note_type)
  let title = MED_REQUEST_TITLE_OVERRIDES[entry.note_type] ?? config.title
  let category: TimelineEventCategory = MED_REQUEST_CATEGORY_OVERRIDES[entry.note_type] ?? config.category

  let fieldChanges: TimelineFieldChange[] | undefined
  if (entry.note_type === 'fields_update' || entry.note_type === 'update') {
    fieldChanges = parseFieldsUpdate(entry.note) ?? undefined
  }

  // Must run before field labels are remapped below, as it checks the raw field name 'cs_notes'
  if (isCsNotesUpdate(entry.note_type, fieldChanges)) {
    category = CS_NOTES_CATEGORY
    title = CS_NOTES_TITLE
  }

  // Remap raw field names to user-friendly labels for display
  const displayFieldChanges = fieldChanges?.map(fc => ({
    ...fc,
    field: formatFieldLabel(fc.field)
  }))

  const description = buildMedRequestTimelineDescription(entry.note_type, entry.note, title, fieldChanges)
  const { source, user } = resolveSourceAndUser(entry)

  return {
    id: `med-request-${medReqHash}-${index}`,
    category,
    source,
    title,
    description,
    timestamp: new Date(entry.created_at),
    user,
    fieldChanges: displayFieldChanges,
    noteType: entry.note_type,
    note: entry.note
  }
}

const INVESTIGATION_TITLE_OVERRIDES: Record<string, string> = {
  fields_update: 'Investigation Updated',
  update: 'Investigation Updated',
  status_update: 'Investigation status updated',
  rest: 'Manual Entry'
}

const INVESTIGATION_CATEGORY_OVERRIDES: Record<string, TimelineEventCategory> = {
  fields_update: 'Investigations',
  update: 'Investigations',
  status_update: 'Investigations',
  rest: 'Investigations'
}

function buildInvestigationTimelineDescription(
  noteType: string,
  note: string,
  configTitle: string,
  fieldChanges?: TimelineFieldChange[]
): string {
  switch (noteType) {
    case 'fields_update':
    case 'update': {
      const parsed = fieldChanges ?? parseFieldsUpdate(note)
      if (parsed) {
        return parsed.length === 1 ? `${parsed[0].field} updated` : `${parsed.length} fields updated`
      }
      break
    }
    case 'status_update': {
      const parsed = parseStatusUpdate(note)
      if (parsed) return `${parsed.previous} → ${parsed.current}`
      break
    }
    case 'email': {
      if (note) return note
      break
    }
  }

  return note || configTitle
}

function toInvestigationTimelineEvent(
  entry: APITimelineEntry,
  index: number,
  investigationHash: string
): TimelineEvent {
  const config = getNoteTypeConfig(entry.note_type)
  const title = INVESTIGATION_TITLE_OVERRIDES[entry.note_type] ?? config.title
  const category: TimelineEventCategory = INVESTIGATION_CATEGORY_OVERRIDES[entry.note_type] ?? config.category

  let fieldChanges: TimelineFieldChange[] | undefined
  if (entry.note_type === 'fields_update' || entry.note_type === 'update') {
    fieldChanges = parseFieldsUpdate(entry.note) ?? undefined
  }

  // Must run before field labels are remapped below, as it checks the raw field name 'cs_notes'
  let resolvedCategory = category
  let resolvedTitle = title
  if (isCsNotesUpdate(entry.note_type, fieldChanges)) {
    resolvedCategory = CS_NOTES_CATEGORY
    resolvedTitle = CS_NOTES_TITLE
  }

  const description = buildInvestigationTimelineDescription(entry.note_type, entry.note, resolvedTitle, fieldChanges)
  const { source, user } = resolveSourceAndUser(entry)

  return {
    id: `investigation-${investigationHash}-${index}`,
    category: resolvedCategory,
    source,
    title: resolvedTitle,
    description,
    timestamp: new Date(entry.created_at),
    user,
    fieldChanges,
    noteType: entry.note_type,
    note: entry.note
  }
}

export type TimelineObjectType = 'patient' | 'order' | 'user' | 'medication_request' | 'investigation'

function getEventMapper(objectType: TimelineObjectType) {
  if (objectType === 'order') return toOrderTimelineEvent
  if (objectType === 'medication_request') return toMedRequestTimelineEvent
  if (objectType === 'investigation') return toInvestigationTimelineEvent
  return toPatientTimelineEvent
}

async function fetchTimeline(hash: string, objectType: TimelineObjectType): Promise<TimelineEvent[]> {
  const result = (await http.get(`/timelines/${hash}`, {
    params: { object_type: objectType }
  })) as APITimelineResponse

  const mapper = getEventMapper(objectType)
  return result.data.map((entry, i) => mapper(entry, i, hash))
}

export const useTimelineQuery = (objectHash: string, objectType: TimelineObjectType, enabled = true) => {
  return useQuery({
    queryKey: ['timeline', objectType, objectHash],
    queryFn: () => fetchTimeline(objectHash, objectType),
    enabled: !!objectHash && enabled
  })
}
